import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, MapPin, User, QrCode, Nfc, ShieldCheck, LifeBuoy, Flag, ExternalLink, History, Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { fmtDateTime } from "../lib/time";
import { Stars, RiskBadge, Avatar } from "./common";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";

function Sec({ title, icon: Icon, children, testId }) {
  return (
    <div className="border-t border-slate-100 pt-4" data-testid={testId}>
      <p className="mb-2 flex items-center gap-1.5 font-head text-sm font-bold text-slate-900">{Icon && <Icon className="h-4 w-4 text-indigo-500" />}{title}</p>
      {children}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1 text-sm">
      <span className="font-semibold text-slate-700">{label}</span>
      <span className="text-right text-slate-600">{children}</span>
    </div>
  );
}

const YesNo = ({ v }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold ${v ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
    {v ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}{v ? "Yes" : "No"}
  </span>
);

export default function FeedbackDetailSheet({ feedbackId, onClose, onChanged }) {
  const [id, setId] = useState(feedbackId);
  const [d, setD] = useState(null);
  useEffect(() => setId(feedbackId), [feedbackId]);
  useEffect(() => {
    if (!id) return;
    setD(null);
    api.get(`/responses/feedback/${id}`).then((r) => setD(r.data)).catch((e) => setD({ error: apiError(e) }));
  }, [id]);

  const moderate = async (status) => {
    try {
      await api.patch(`/feedback/${id}/moderate`, { review_status: status, reason: `Manual review → ${status}` });
      toast.success(`Marked ${status.replace("_", " ")}`);
      setD((cur) => ({ ...cur, feedback: { ...cur.feedback, review_status: status } }));
      onChanged && onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Sheet open={!!feedbackId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="feedback-detail-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="font-head text-xl font-bold text-slate-900">Rating details</SheetTitle>
          <SheetDescription>Everything this customer submitted, plus trust and follow-up history.</SheetDescription>
        </SheetHeader>
        {!d ? <p className="py-16 text-center text-sm text-slate-400">Loading…</p>
          : d.error ? <p className="py-16 text-center text-sm text-slate-400" data-testid="feedback-detail-error">{d.error}</p>
          : <Body d={d} moderate={moderate} openOther={setId} onClose={onClose} />}
      </SheetContent>
    </Sheet>
  );
}

function Body({ d, moderate, openOther, onClose }) {
  const f = d.feedback;
  return (
    <div className="mt-5 space-y-4">
      <div className="rounded-xl bg-slate-50 p-4" data-testid="fd-header">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2"><Stars value={f.rating} size={22} /><span className="font-head text-2xl font-bold text-slate-900" data-testid="fd-rating">{f.rating}/5</span></div>
          <RiskBadge status={f.review_status} testId="fd-risk" />
        </div>
        <p className="mt-2 text-xs text-slate-500" data-testid="fd-datetime">{fmtDateTime(f.created_at, f.location_id)}{d.location?.timezone ? ` · ${d.location.timezone}` : ""}</p>
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          <span className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 font-semibold text-slate-600">{f.source === "nfc" ? <Nfc className="h-3.5 w-3.5" /> : <QrCode className="h-3.5 w-3.5" />}{(f.source || "qr").toUpperCase()} scan</span>
          {f.review_clicked && <span className="rounded-full bg-indigo-50 px-2.5 py-1 font-semibold text-indigo-700" data-testid="fd-review-clicked">Clicked Google review</span>}
          {f.point_name && <span className="rounded-full bg-violet-50 px-2.5 py-1 font-semibold capitalize text-violet-700" data-testid="fd-point">Via {f.point_type}: {f.point_name}</span>}
          {f.has_dispute && <span className="rounded-full bg-amber-50 px-2.5 py-1 font-semibold text-amber-700">Disputed</span>}
        </div>
      </div>

      <Sec title="Who & where" icon={User} testId="fd-who">
        {d.employee && (
          <Link to={`/app/employees/${d.employee.id}`} onClick={onClose} className="flex items-center gap-3 rounded-lg p-2 hover:bg-slate-50" data-testid="fd-employee-link">
            <Avatar name={d.employee.name} src={d.employee.photo_url} size={40} />
            <div className="min-w-0 flex-1"><p className="font-bold text-slate-900">{d.employee.name}</p><p className="text-xs text-slate-500">{d.employee.job_title || "—"}{d.employee.status !== "active" ? " · former staff" : ""}</p></div>
            <ExternalLink className="h-4 w-4 text-slate-300" />
          </Link>
        )}
        {d.location && (
          <Link to={`/app/store?location=${d.location.id}`} onClick={onClose} className="mt-1 flex items-center gap-2 rounded-lg p-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="fd-location-link">
            <MapPin className="h-4 w-4 text-slate-400" />{d.location.name}<ExternalLink className="ml-auto h-4 w-4 text-slate-300" />
          </Link>
        )}
      </Sec>

      <Answers d={d} />

      {f.comment && (
        <Sec title="Comment" icon={MessageSquare} testId="fd-comment">
          <p className="rounded-lg border-l-4 border-indigo-200 bg-indigo-50/40 px-3 py-2 text-sm italic text-slate-700">“{f.comment}”</p>
          {d.themes.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5">{d.themes.map((t) => <span key={t.key} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">{t.label}</span>)}</div>}
        </Sec>
      )}

      {(f.problem_category || f.resolved_on_site !== null && f.resolved_on_site !== undefined) && (
        <Sec title="What went wrong" icon={Flag} testId="fd-problem">
          {f.problem_category && <Row label="Problem">{f.problem_category}</Row>}
          {f.resolved_on_site !== null && f.resolved_on_site !== undefined && <Row label="Resolved at the time?"><YesNo v={f.resolved_on_site} /></Row>}
        </Sec>
      )}

      <Recovery d={d} onClose={onClose} />
      <Visitor v={d.visitor} openOther={openOther} />

      <Sec title="Trust & integrity" icon={ShieldCheck} testId="fd-trust">
        <Row label="Trust score">{f.trust_score != null ? `${Math.round(f.trust_score * 100)}%` : "—"}</Row>
        <Row label="Signals">{f.trust_signals?.length ? f.trust_signals.join(", ") : "None"}</Row>
        {f.moderation_reason && <Row label="Last review">{f.moderation_reason}{f.moderated_at ? ` · ${fmtDateTime(f.moderated_at, f.location_id)}` : ""}</Row>}
        <div className="mt-3 flex gap-2">
          <Button size="sm" variant="outline" onClick={() => moderate("TRUSTED")} data-testid="fd-mark-trusted">Mark trusted</Button>
          <Button size="sm" variant="outline" className="text-red-600" onClick={() => moderate("REJECTED")} data-testid="fd-reject">Reject</Button>
        </div>
      </Sec>
    </div>
  );
}

function Answers({ d }) {
  const qa = (d.feedback.question_answers || []).filter((a) => a.value !== null && a.value !== undefined);
  const skipped = (d.feedback.question_answers || []).length - qa.length;
  if (!qa.length && !d.dimensions.length && !d.touchpoints.length) {
    return <Sec title="Question responses" testId="fd-answers"><p className="text-sm text-slate-400">No questions answered.</p></Sec>;
  }
  return (
    <Sec title="Question responses" testId="fd-answers">
      {qa.map((a) => <Row key={a.q} label={a.q}><YesNo v={a.value} /></Row>)}
      {d.touchpoints.map((t) => <Row key={t.key} label={t.label}><YesNo v={t.value} /></Row>)}
      {d.dimensions.map((dm) => <Row key={dm.key} label={dm.label}><Stars value={dm.value} size={14} /></Row>)}
      {skipped > 0 && <p className="mt-1 text-xs text-slate-400">{skipped} question{skipped === 1 ? "" : "s"} skipped</p>}
    </Sec>
  );
}

function Recovery({ d, onClose }) {
  const c = d.recovery_case, ds = d.dispute;
  if (!c && !ds) return null;
  return (
    <Sec title="Follow-up" icon={LifeBuoy} testId="fd-followup">
      {c && (
        <>
          <Row label="Recovery case"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-700">{c.status}</span></Row>
          {c.contact_detail && <Row label="Customer contact">{c.contact_name ? `${c.contact_name} · ` : ""}{c.contact_detail}</Row>}
          <Row label="Follow-up allowed">{c.allow_follow_up ? "Yes" : "No"}</Row>
          {c.outcome && <Row label="Outcome">{c.outcome}</Row>}
          <Link to="/app/recovery" onClick={onClose} className="mt-1 inline-flex items-center gap-1 text-sm font-bold text-indigo-700 hover:text-indigo-900" data-testid="fd-recovery-link">Open recovery cases <ExternalLink className="h-3.5 w-3.5" /></Link>
        </>
      )}
      {ds && (
        <div className="mt-2">
          <Row label="Dispute">{ds.dispute_status}{ds.decision ? ` · ${ds.decision}` : ""}</Row>
          <Row label="Reason">{ds.reason}</Row>
        </div>
      )}
    </Sec>
  );
}

function Visitor({ v, openOther }) {
  return (
    <Sec title="This customer" icon={History} testId="fd-visitor">
      {!v ? <p className="text-sm text-slate-400">No visitor history (older rating or private browsing).</p> : (
        <>
          <Row label="Ratings from this browser">{v.total_ratings} · avg {v.avg_rating}</Row>
          <Row label="QR / NFC scans">{v.scans}</Row>
          <Row label="First seen">{fmtDateTime(v.first_seen)}</Row>
          {v.others.length > 0 && (
            <div className="mt-2 space-y-1">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Other ratings</p>
              {v.others.map((o) => (
                <button key={o.id} onClick={() => openOther(o.id)} data-testid={`fd-other-${o.id}`}
                  className="flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-left text-sm hover:bg-slate-50">
                  <span className="flex items-center gap-2"><Stars value={o.rating} size={13} /><span className="font-semibold text-slate-700">{o.employee_name || "—"}</span></span>
                  <span className="text-xs text-slate-400">{fmtDateTime(o.created_at, o.location_id)}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </Sec>
  );
}
